import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import './TopBar.css';
import { clearTopBarMeasurement, setTopBarMeasurement } from './floatingTopStack';
import { isPageScrollActive } from './pageScroll';

const TopBar = ({ children, accentColor }) => {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(window.scrollY);
  const appScrollActive = useRef(isPageScrollActive());
  const forcedHiddenUntilMs = useRef(0);
  const topBarRef = useRef(null);
  const measurementOwnerRef = useRef(Symbol('top-bar-measurement'));

  useEffect(() => {
    const handleCollapse = () => {
      setIsVisible(false);
    };

    const handleLockHidden = (e) => {
      const durationMs = Number(e?.detail?.durationMs);
      const safeDuration = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 1200;
      forcedHiddenUntilMs.current = Math.max(forcedHiddenUntilMs.current, Date.now() + safeDuration);
      setIsVisible(false);
    };

    window.addEventListener('regimen:topbar:collapse', handleCollapse);
    window.addEventListener('regimen:topbar:lockHidden', handleLockHidden);

    return () => {
      window.removeEventListener('regimen:topbar:collapse', handleCollapse);
      window.removeEventListener('regimen:topbar:lockHidden', handleLockHidden);
    };
  }, []);

  useEffect(() => {
    // A sibling route effect can start navigation after render, before this
    // listener mounts. Read the current transaction and restored offset here.
    appScrollActive.current = isPageScrollActive();
    lastScrollY.current = window.scrollY;
    const handleScroll = () => {
      // Freezing the body resets window.scrollY to zero. That is not an upward
      // scroll: preserve both visibility and the last real position until the
      // final modal closes and ModalProvider restores the document scroll.
      if (document.body.classList.contains('modal-open')) return;

      const currentScrollY = window.scrollY;
      const previousScrollY = lastScrollY.current;
      lastScrollY.current = currentScrollY;

      if (Date.now() < forcedHiddenUntilMs.current) {
        setIsVisible(false);
        return;
      }
      // App navigation is not manual intent. Keep the baseline current without
      // forcing either visible or hidden (including a programmatic trip to 0).
      if (appScrollActive.current) return;

      if (currentScrollY < previousScrollY || currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > previousScrollY && currentScrollY > 50) {
        setIsVisible(false);
      }
    };

    const handleAppScroll = event => {
      appScrollActive.current = event.detail.active;
      if (!document.body.classList.contains('modal-open')) lastScrollY.current = window.scrollY;
    };
    window.addEventListener('regimen:page-scroll', handleAppScroll);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('regimen:page-scroll', handleAppScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const owner = measurementOwnerRef.current;
    const topBarElement = topBarRef.current;
    let transitionFrame = null;
    const syncTopBarHeight = () => {
      const rect = topBarElement?.getBoundingClientRect();
      const visibleHeight = rect ? Math.max(0, Math.min(rect.height, rect.bottom)) : 0;
      setTopBarMeasurement(owner, visibleHeight);
    };
    const trackTransition = startTime => {
      const step = timestamp => {
        syncTopBarHeight();
        if (timestamp - startTime < 360) transitionFrame = requestAnimationFrame(step);
      };
      transitionFrame = requestAnimationFrame(step);
    };

    syncTopBarHeight();
    trackTransition(performance.now());
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncTopBarHeight)
      : null;
    if (topBarElement) resizeObserver?.observe(topBarElement);
    topBarElement?.addEventListener('transitionend', syncTopBarHeight);
    window.addEventListener('resize', syncTopBarHeight);
    return () => {
      if (transitionFrame) cancelAnimationFrame(transitionFrame);
      resizeObserver?.disconnect();
      topBarElement?.removeEventListener('transitionend', syncTopBarHeight);
      window.removeEventListener('resize', syncTopBarHeight);
      clearTopBarMeasurement(owner);
    };
  }, [isVisible]);

  return (
    <div ref={topBarRef} className={`top-bar ${isVisible ? '' : 'hidden'}`}
      data-dojo-accent={accentColor ? '' : undefined}
      style={accentColor ? { '--dojo-topbar-accent': accentColor } : undefined}>
      <div className="top-bar-items">
        {children}
      </div>
    </div>
  );
};

export default TopBar;